import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  findFieldResolvers,
  findFieldsLike,
  findJsxUsages,
  findOperationCallsites,
  findOperationsLike,
  lookupOperation,
} from '../lib/api';
import type { Theme } from '../lib/shiki';
import { CodeBlock } from '../components/CodeBlock';

type Tool =
  | 'graphql_operation'
  | 'find_field_resolvers'
  | 'find_fields_like'
  | 'find_operations_like'
  | 'find_operation_callsites'
  | 'find_jsx_usages';

interface Props {
  theme: Theme;
}

const TOOL_LABELS: Record<Tool, string> = {
  graphql_operation: 'graphql_operation',
  find_field_resolvers: 'find_field_resolvers',
  find_fields_like: 'find_fields_like',
  find_operations_like: 'find_operations_like',
  find_operation_callsites: 'find_operation_callsites',
  find_jsx_usages: 'find_jsx_usages',
};

const TOOL_DESCRIPTIONS: Record<Tool, string> = {
  graphql_operation: 'Look up by name. Qualified (Query.me) → backend resolver; bare (GetMe) → FE operation.',
  find_field_resolvers: 'Does X.Y exist? Exact match on type + field.',
  find_fields_like: 'Any field roughly named like X on type Y? (case-insensitive, * wildcard)',
  find_operations_like: 'FE operations matching a pattern (* wildcard).',
  find_operation_callsites: 'Every FE file that uses an operation by name.',
  find_jsx_usages: 'Every JSX <Identifier ...> usage of a PascalCase component name.',
};

/** The structural panel — deliberately styled distinct from the semantic-search page so users
 *  don't confuse "I know the name" with "I'm searching by meaning". Mauve/violet background,
 *  no result-row scoring or feedback buttons, JSON-shaped output by default. */
export function StructuralPage({ theme }: Props) {
  const [tool, setTool] = useState<Tool>('graphql_operation');
  const [arg1, setArg1] = useState('');
  const [arg2, setArg2] = useState('');
  const [output, setOutput] = useState<unknown>(null);

  const mutation = useMutation({
    mutationFn: async (): Promise<unknown> => {
      switch (tool) {
        case 'graphql_operation':
          return await lookupOperation(arg1);
        case 'find_field_resolvers':
          return await findFieldResolvers(arg1, arg2);
        case 'find_fields_like':
          return await findFieldsLike(arg1, arg2);
        case 'find_operations_like':
          return await findOperationsLike(arg1);
        case 'find_operation_callsites':
          return await findOperationCallsites(arg1);
        case 'find_jsx_usages':
          return await findJsxUsages(arg1);
      }
    },
    onSuccess: (data) => setOutput(data),
    onError: (err) => setOutput({ error: (err as Error).message }),
  });

  const needsTwoArgs = tool === 'find_field_resolvers' || tool === 'find_fields_like';
  const arg1Label = labelForArg1(tool);
  const arg2Label = labelForArg2(tool);

  return (
    <main className="flex-1 min-w-0 overflow-y-auto px-4 py-4 bg-violet-50/30 dark:bg-violet-950/10">
      <div className="max-w-3xl mx-auto">
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-1">Structural lookup</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Deterministic SQL over the bridge data. Use when you know the name — no semantic
            guessing, no scores, just hit-or-miss.
          </p>
        </div>

        <div className="rounded-lg border border-violet-200 dark:border-violet-900 bg-white dark:bg-zinc-900 p-3 mb-4">
          <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
            Tool
          </label>
          <select
            value={tool}
            onChange={(e) => {
              setTool(e.target.value as Tool);
              setArg1('');
              setArg2('');
              setOutput(null);
            }}
            className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm font-mono"
          >
            {(Object.keys(TOOL_LABELS) as Tool[]).map((t) => (
              <option key={t} value={t}>
                {TOOL_LABELS[t]}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{TOOL_DESCRIPTIONS[tool]}</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!arg1.trim()) return;
              if (needsTwoArgs && !arg2.trim()) return;
              mutation.mutate();
            }}
            className="mt-3 flex gap-2 flex-wrap"
          >
            <input
              value={arg1}
              onChange={(e) => setArg1(e.target.value)}
              placeholder={arg1Label}
              className="flex-1 min-w-[200px] rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm font-mono"
            />
            {needsTwoArgs && (
              <input
                value={arg2}
                onChange={(e) => setArg2(e.target.value)}
                placeholder={arg2Label}
                className="flex-1 min-w-[200px] rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm font-mono"
              />
            )}
            <button
              type="submit"
              disabled={mutation.isPending || !arg1.trim() || (needsTwoArgs && !arg2.trim())}
              className="rounded bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-1.5"
            >
              {mutation.isPending ? 'Looking up…' : 'Look up'}
            </button>
          </form>
        </div>

        {output !== null && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
            <h3 className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Result</h3>
            <CodeBlock
              code={JSON.stringify(output, null, 2)}
              language="json"
              theme={theme}
            />
          </div>
        )}
      </div>
    </main>
  );
}

function labelForArg1(tool: Tool): string {
  switch (tool) {
    case 'graphql_operation':
      return 'operation name (Query.me, User.unreadCount, GetMe…)';
    case 'find_field_resolvers':
      return 'type name (User, Query, …)';
    case 'find_fields_like':
      return 'type name (User, Query, …)';
    case 'find_operations_like':
      return 'pattern (Get*Profile, *Auth*)';
    case 'find_operation_callsites':
      return 'operation name (GetMe)';
    case 'find_jsx_usages':
      return 'component identifier (MyAccount, NavBar)';
  }
}

function labelForArg2(tool: Tool): string {
  switch (tool) {
    case 'find_field_resolvers':
      return 'field name (hasUnreadMessages)';
    case 'find_fields_like':
      return 'pattern (unread*count*)';
    default:
      return '';
  }
}
